<template>
  <div class="loading-container">
    <div>
      <TicketWaitingV2 class="queue w-100"
        :showingClients="this.showingClients" 
      />
    </div>
    <div class="d-flex align-items-center gap-1 flex-wrap">
      <template v-for="client in showingClients" :key="client.name">
          <a  v-if="client?.waitingNoc > 0" :href="client?.waitingUrl" target="_blank" class="btn text-white btn-sm" :class="client.colorClass">
              <div class="d-flex align-items-center gap-1" ><div class="uppercase"> {{client.name }} </div>: {{ client.waitingNoc }} <Icon icon="mdi:message-processing"/></div>
          </a> 
      </template>
    </div>
    <br></br>

    <div class="content w-100" v-for="client in showingClients" :key="client.name">
      <div class="title"> 
          <span class="uppercase">{{ client.name }}</span>
          <div class="sla-info">
            <div
              class="text-white"
              type="button"
            > 
          </div>
      </div>
      </div>
      <div v-if="client.problems && client.problems.length">
          <ZabbixProblems 
            :problems="client.problems" 
            :modalsOpened="client.modalsOpened"
            :whatsappSentProblems="client.whatsappSentProblems"
            :problemsPerPage="problemsPerPage"
            :title="client.name" 
            :openTicketType="configs.openTicketType" 
            :companyId="client.id"
            :error="client.error"
            :lastUpdate="client.lastUpdate"
            :isMonitoring="this.isMonitoring"
            @open-modal="handleOpenModal"
            @close-modal="handleCloseModal"
          />
      </div>
    </div>
    <div class="spinner-grow text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
</template>

<script>
import ZabbixProblems from '@/pages/monitoring/ZabbixProblems.vue';
import QueueValues from '@/pages/monitoring/QueueValues.vue';
import TicketWaitingV2 from '@/pages/monitoring/TicketWaitingV2.vue';
import { updateTime, problemsPerPage } from '@/configs/general';
import { formateDateDiaHora, isWithinPolicy } from '../../../../zabbix-shared/utils/time';
import { CompanyService, waitingService } from '@/services/backend/ApiService';
import { mapRoutesClients } from '@/configs/mapConfigRoutes';
import { ServerMessageType, ClientMessageType } from '../../../../zabbix-shared/enums/socketMessage';
import { backendSocket, metroCompanyId} from '../../../../zabbix-shared/env';
import { isMetroUser } from '../../../../zabbix-shared/utils/user';
import UserService from '@/services/OnlineUserService';
import { Icon } from '@iconify/vue';
import { getColorClass } from '@/services/utils';

export default {
  components: {
    ZabbixProblems,
    QueueValues,
    TicketWaitingV2,
    Icon
  },
  data() {
    return {
      user: null,
      updateTime,
      problemsPerPage,
      interval: null,
      companies: [],
      showingClients: [],
      metroId: metroCompanyId,
      socket: null,
      queuesColors: [
          {
              colorClass: "bg-problem",
              quantity: -5
          },
          {
              colorClass: "bg-fine",
              quantity: 1,
          },
          {
              colorClass: "bg-warning",
              quantity: 5
          },
          {
              colorClass: "bg-danger",
              quantity: 10
          }
      ]
    };
  },
  async mounted() {
    this.companies = await CompanyService.findAll();
    this.user = await UserService.waitForUser()
    const reconnectDelay = 5000;

    const connectSocket = () => {
      if (this.socket) {
        this.socket.close();
      }
      const socket = new WebSocket(backendSocket);
      this.socket = socket;
      this.updateShowingClients();
      socket.addEventListener('open', () => {
        socket.send(JSON.stringify({
          type: ClientMessageType.SUBSCRIBE,
          serving: !this.isMonitoring,
          monitoring: this.isMonitoring
        }));
      });

      socket.addEventListener('message', (event) => {
        const data = JSON.parse(event.data);

        if(data.type == ServerMessageType.PROBLEMS){
          const client = this.showingClients.find((client)=>{
            return client.id == data.company.id;
          })
          
          if (client && !data.error) {
            if(client.problems == undefined){
              client.problems = [];
            }

            const otherZabbixProblems = client.problems.filter(problem => problem.zabbixId != data.zabbixId);
            client.problems= [...data.problems, ...otherZabbixProblems];
            client.problems.sort((a, b) => Number(b.clock) - Number(a.clock));
            client.lastUpdate = formateDateDiaHora(Date.now());
          } 

          if(client && data.error) {
            client.error = data.error;
          }
        }

        if(data.type == ServerMessageType.MODALS){
          this.showingClients.forEach((client) => {
            const companyZabbixProblems = data.modals.filter(data => data.company.id == client.id);
            client.modalsOpened = companyZabbixProblems ?? [];
          });
        }

        if(data.type == ServerMessageType.WHATSAPP_SENT_PROBLEMS){
          this.showingClients.forEach((client) => {
            const companyWhatsappSentProblems = data.whatsappSentProblems.filter(data => data.company.id == client.id);
            client.whatsappSentProblems = companyWhatsappSentProblems ?? [];
            console.log(client.whatsappSentProblems)
          });
        }
      });

      socket.addEventListener('close', () => {
        setTimeout(connectSocket, reconnectDelay);
      });
    };

    connectSocket();

    this.interval = setInterval(() => {
      this.updateShowingClients();
    }, updateTime)
  },
  methods: {
    updateShowingClients() {
      this.showingClients = [];

      for (let client of this.companies) {
        let addClient = true;

        if (client.scheduleMonitoring && this.isMonitoring) {
          addClient = isWithinPolicy(client.scheduleMonitoring?.intervals);
        }

        if (client.scheduleService && !this.isMonitoring) {
          addClient = isWithinPolicy(client.scheduleService?.intervals);
        }

        if (addClient || !isMetroUser(this.user)) {
          this.showingClients.push(client)
        }
      }

      for (let client of this.showingClients) {
        let queues = [];
        
        for (let queue of client.queues) {
          if (this.addToScreen(queue)) {
            queues.push(queue);
          }
        }

        let tickets = [];
        for (let ticket of client.tickets) {
          if (this.addToScreen(ticket)) {
            tickets.push(ticket);
          }
        }

        client.queues = queues;
        client.tickets = tickets;
      }

       this.showingClients.forEach(async (client) => {
        if(client.queues[0]) {
          const filter =  {select: { quantity: true}, client: client.queues[0]?.client, order: { createdAt: 'desc'}, limit: 1};
          client.waitingNoc = (await waitingService.findAll(filter))[0]?.quantity;
          client.waitingUrl = client.queues[0]?.url;
          client.colorClass = getColorClass(client.waitingNoc, this.queuesColors)
        }
      });
    },
    addToScreen(element){
      return (element.showMonitoring && this.isMonitoring) || (element.showService && !this.isMonitoring)
    },
    handleOpenModal(payload) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({
          type: ClientMessageType.OPEN_MODAL,
          modal: payload
        }));
      }
    },
    handleCloseModal(payload) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({
          type: ClientMessageType.CLOSE_MODAL,
          modal: payload
        }));
      }
    },
  },
  beforeUnmount() {
    clearInterval(this.interval);
    if (this.socket) {
      this.socket.close();
    }
  },
  watch: {
    '$route.params.groups': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateShowingClients();
      }
    },
  },
  computed: {
    configs() {
      return mapRoutesClients[this.$route.params.groups]
    },
    isMonitoring() {
      return this.$route.params.groups == 'monitoramento'
    }
  }
};
</script>

<style scoped>
body,
html,
.content {
  background: rgb(255, 255, 255);
}

.title {
  display: none;
  position: relative;
}

.content:has(.table, .alert) .title {
  display: flex;
}

.alarm-error {
  background-color: gray;
}

.loading-container {
  position: relative;
}

.loading-container:has(.table, .alert) .spinner-grow {
  display: none
}

.spinner-grow {
  position: absolute;
  left: 50%;
  top: 50vh;
}

.alert {
  height: 50px;
}

.sla-info{
  position:absolute;
  font-size: 18px;
  top: 7px;
  right: 20px;
}


.bg-problem {
  background-color: #616161 !important; 
}
.bg-fine {
  background-color: #00beaa !important;
}
.bg-warning {
  background-color: #ff9600 !important; 
}
.bg-danger {
  background-color: #e53935 !important; 
}

</style>